<template>
  <div class="space-y-4">
    <div>
      <div class="text-center">
        <!-- Empty State - Upload Prompt -->
        <Label
          html-for="photo-upload"
          class="relative cursor-pointer block min-h-40 mx-auto rounded-lg overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors group"
        >
          <div class="absolute inset-0 flex flex-col items-center justify-center p-4">
            <Camera class="h-12 w-12 text-gray-400 group-hover:text-gray-600 mb-2" />
            <span class="text-sm font-medium text-gray-700">
              {{ allowMultiple ? 'Add profile photos' : 'Add your profile photo' }}
            </span>
            <span class="text-xs text-gray-500 mt-1">
              {{ allowMultiple ? 'Choose one or more images' : 'Choose one image' }}
            </span>
          </div>
          <input
            id="photo-upload"
            ref="fileInput"
            type="file"
            accept="image/*"
            :multiple="allowMultiple"
            class="sr-only"
            :disabled="isUploading"
            @change="handleFileSelect"
          >
        </Label>
      </div>

      <div v-if="photos.length > 0" class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div
          v-for="photo in photos"
          :key="photo.id"
          class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div class="relative aspect-square bg-gray-100">
            <img :src="photo.photo_url" alt="Profile photo" class="h-full w-full object-cover">

            <div class="absolute left-2 top-2 rounded bg-black/65 px-2 py-1 text-xs font-medium text-white">
              {{ photo.is_thumbnail || photo.is_primary ? 'Main' : `#${(photo.order ?? 0) + 1}` }}
            </div>
          </div>

          <div v-if="allowMultiple" class="grid grid-cols-2 gap-2 p-2">
            <button
              type="button"
              class="inline-flex min-h-10 items-center justify-center rounded border border-gray-300 bg-white px-2 text-xs font-medium text-gray-800 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-green-200 disabled:bg-green-50 disabled:text-green-700"
              :disabled="isUploading || photo.is_thumbnail || photo.is_primary"
              :aria-label="photo.is_thumbnail || photo.is_primary ? 'Main photo' : 'Set as main photo'"
              @click="setMainPhoto(photo)"
            >
              <Star class="mr-1 h-4 w-4" />
              {{ photo.is_thumbnail || photo.is_primary ? 'Main' : 'Set main' }}
            </button>
            <button
              type="button"
              class="inline-flex min-h-10 items-center justify-center rounded border border-red-200 bg-white px-2 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isUploading"
              aria-label="Remove photo"
              @click="deletePhoto(photo)"
            >
              <Trash2 class="mr-1 h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div v-if="photos.length > 0" class="mt-3 text-center">
        <Label
          html-for="photo-upload-more"
          class="inline-flex cursor-pointer items-center justify-center rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        >
          <Camera class="mr-2 h-4 w-4" />
          {{ allowMultiple ? 'Add more photos' : 'Update photo' }}
          <input
            id="photo-upload-more"
            type="file"
            accept="image/*"
            :multiple="allowMultiple"
            class="sr-only"
            :disabled="isUploading"
            @change="handleFileSelect"
          >
        </Label>
      </div>

      <!-- Upload Progress -->
      <div v-if="isUploading" class="mt-4">
        <div class="flex items-center justify-center mb-2">
          <Loader2 class="h-5 w-5 animate-spin text-rogue-vibrant-green mr-2" />
          <span class="text-sm text-gray-600">{{ uploadStatus }}</span>
        </div>
        <div class="w-48 mx-auto bg-gray-200 rounded-full h-2">
          <div
            class="bg-rogue-vibrant-green h-2 rounded-full transition-all duration-300"
            :style="{ width: uploadProgress + '%' }"
          />
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-center text-red-700">
          <AlertCircle class="h-4 w-4 mr-2 flex-shrink-0" />
          <span class="text-sm">{{ error }}</span>
        </div>
      </div>

      <!-- Help Text -->
      <p class="mt-3 text-xs text-gray-500 text-center">
        {{ allowMultiple
          ? 'Share JPG, PNG, GIF or WebP • Up to 10MB each • First photo is used as your main image'
          : 'Share JPG, PNG, GIF or WebP • Up to 10MB • You can add more photos after onboarding'
        }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/auth'
import { Camera, Loader2, AlertCircle, Star, Trash2 } from 'lucide-vue-next'
import Label from './ui/Label.vue'

const props = defineProps({
  currentPhotoUrl: String,
  photos: {
    type: Array,
    default: () => []
  },
  allowMultiple: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['photo-uploaded', 'upload-error'])

const authStore = useAuthStore()

// State
const photos = ref([...props.photos])
const isUploading = ref(false)
const uploadProgress = ref(0)
const uploadStatus = ref('Preparing upload...')
const error = ref('')

// Watch for prop changes
watch(
  () => props.photos,
  newPhotos => {
    photos.value = [...newPhotos]
  },
  { deep: true }
)

watch(
  () => props.currentPhotoUrl,
  newUrl => {
    if (!newUrl || photos.value.some(photo => photo.photo_url === newUrl)) return
    photos.value = [
      {
        id: newUrl,
        photo_url: newUrl,
        is_primary: true,
        is_thumbnail: true,
        order: 0
      },
      ...photos.value
    ]
  }
)

// Image compression function
async function compressImage(file, maxWidth = 1200, quality = 0.8) {
  return new Promise(resolve => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }

      canvas.width = width
      canvas.height = height

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob(resolve, 'image/jpeg', quality)
    }

    img.src = URL.createObjectURL(file)
  })
}

// Enhanced file validation
function validateFile(file) {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  if (!allowedTypes.includes(file.type)) {
    return 'Please select a valid image file (JPG, PNG, GIF, or WebP)'
  }

  if (file.size > 10 * 1024 * 1024) {
    return 'File size must be less than 10MB'
  }

  return null
}

function getStoragePath(photoUrl) {
  try {
    const url = new URL(photoUrl)
    const marker = '/profile-photos/'
    const markerIndex = url.pathname.indexOf(marker)
    if (markerIndex >= 0) {
      return decodeURIComponent(url.pathname.slice(markerIndex + marker.length))
    }

    const pathSegments = url.pathname.split('/')
    const fileName = pathSegments.pop()
    const folderName = pathSegments.pop()
    return `${folderName}/${fileName}`
  } catch {
    return null
  }
}

async function updateProfileThumbnail(photoUrl) {
  const { error: profileError } = await supabase
    .from('profiles')
    .update({
      thumbnail_url: photoUrl,
      updated_at: new Date().toISOString()
    })
    .eq('id', authStore.user.id)

  if (profileError) {
    console.error('Profile update error:', profileError)
    throw new Error(`Profile update failed: ${profileError.message}`)
  }
}

async function refreshPhotos() {
  try {
    const { data, error: photoError } = await supabase
      .from('profile_photos')
      .select('*')
      .eq('user_id', authStore.user.id)
      .order('order', { ascending: true })
      .order('created_at', { ascending: true })

    if (photoError) throw photoError
    photos.value = data || []
  } catch (refreshError) {
    console.warn('Photo refresh warning:', refreshError)
  }
}

async function syncMainPhoto() {
  if (photos.value.length === 0) {
    await updateProfileThumbnail(null)
    return
  }

  const mainPhoto = photos.value.find(photo => photo.is_thumbnail || photo.is_primary) || photos.value[0]
  await setMainPhoto(mainPhoto, false)
}

async function setMainPhoto(photo, shouldEmit = true) {
  if (!authStore.user || !photo) return

  isUploading.value = true
  uploadStatus.value = 'Updating main photo...'
  uploadProgress.value = 50
  error.value = ''

  try {
    const { error: clearError } = await supabase
      .from('profile_photos')
      .update({ is_primary: false, is_thumbnail: false })
      .eq('user_id', authStore.user.id)

    if (clearError) throw clearError

    const { error: setError } = await supabase
      .from('profile_photos')
      .update({ is_primary: true, is_thumbnail: true, order: 0 })
      .eq('id', photo.id)
      .eq('user_id', authStore.user.id)

    if (setError) throw setError

    await updateProfileThumbnail(photo.photo_url)
    await refreshPhotos()

    uploadProgress.value = 100
    if (shouldEmit) {
      emit('photo-uploaded', photo.photo_url)
    }
  } catch (err) {
    console.error('Main photo update failed:', err)
    error.value = err.message || 'Failed to update main photo'
    emit('upload-error', error.value)
  } finally {
    isUploading.value = false
    uploadProgress.value = 0
    uploadStatus.value = 'Preparing upload...'
  }
}

async function deleteExistingPhotos() {
  const existingPhotos = [...photos.value]

  if (existingPhotos.length > 0) {
    const { error: dbError } = await supabase
      .from('profile_photos')
      .delete()
      .eq('user_id', authStore.user.id)

    if (dbError) throw dbError
  }

  const storagePaths = existingPhotos.map(photo => getStoragePath(photo.photo_url)).filter(Boolean)
  if (storagePaths.length > 0) {
    const { error: storageError } = await supabase.storage.from('profile-photos').remove(storagePaths)
    if (storageError) {
      console.warn('Storage cleanup warning:', storageError)
    }
  }

  photos.value = []
}

// File handling
async function handleFileSelect(event) {
  const selectedFiles = Array.from(event.target.files || [])
  const filesToUpload = props.allowMultiple ? selectedFiles : selectedFiles.slice(0, 1)
  if (filesToUpload.length === 0) return

  error.value = ''

  // Validate file
  for (const file of filesToUpload) {
    const validationError = validateFile(file)
    if (validationError) {
      error.value = validationError
      return
    }
  }

  await uploadPhotos(filesToUpload)

  // Clear the input so the same file can be selected again
  event.target.value = ''
}

async function uploadPhotos(files) {
  if (!authStore.user) {
    error.value = 'Please log in to upload photos'
    return
  }

  isUploading.value = true
  uploadProgress.value = 0
  uploadStatus.value = 'Preparing upload...'

  try {
    if (!props.allowMultiple) {
      uploadStatus.value = 'Replacing profile photo...'
      uploadProgress.value = 5
      await deleteExistingPhotos()
    }

    const currentMaxOrder = photos.value.reduce((max, photo) => Math.max(max, photo.order ?? 0), -1)
    let firstUploadedUrl = ''

    for (const [index, file] of files.entries()) {
      const baseProgress = Math.round((index / files.length) * 90)
      uploadStatus.value = `Compressing image ${index + 1} of ${files.length}...`
      uploadProgress.value = baseProgress + 10

      const compressedFile = await compressImage(file)
      const fileExt = file.name.split('.').pop()
      const fileName = `${authStore.user.id}/${Date.now()}-${index}-profile.${fileExt}`

      uploadStatus.value = `Uploading image ${index + 1} of ${files.length}...`

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, compressedFile, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        console.error('Storage upload error:', uploadError)
        throw new Error(`Upload failed: ${uploadError.message}`)
      }

      const {
        data: { publicUrl }
      } = supabase.storage.from('profile-photos').getPublicUrl(fileName)

      const isFirstPhoto = photos.value.length === 0 && index === 0
      const { error: dbError } = await supabase.from('profile_photos').insert({
        user_id: authStore.user.id,
        photo_url: publicUrl,
        is_primary: isFirstPhoto,
        is_thumbnail: isFirstPhoto,
        order: currentMaxOrder + index + 1,
        processing_status: 'completed'
      })

      if (dbError) {
        console.error('Database error:', dbError)
        throw new Error(`Database save failed: ${dbError.message}`)
      }

      if (!firstUploadedUrl) {
        firstUploadedUrl = publicUrl
      }

      if (isFirstPhoto) {
        await updateProfileThumbnail(publicUrl)
      }
    }

    uploadProgress.value = 100
    uploadStatus.value = files.length === 1 ? 'Upload complete!' : 'Uploads complete!'
    await refreshPhotos()

    // Emit success
    emit('photo-uploaded', firstUploadedUrl)
  } catch (err) {
    console.error('Upload failed:', err)
    error.value = err.message || 'Failed to upload photo. Please try again.'
    emit('upload-error', err.message || 'Upload failed')
  } finally {
    setTimeout(() => {
      isUploading.value = false
      uploadProgress.value = 0
      uploadStatus.value = 'Preparing upload...'
    }, 1000) // Show completion for a moment
  }
}

async function deletePhoto(photo) {
  if (!authStore.user || !photo) return

  isUploading.value = true
  uploadStatus.value = 'Removing photo...'
  uploadProgress.value = 50
  error.value = ''

  try {
    const { error: dbError } = await supabase
      .from('profile_photos')
      .delete()
      .eq('id', photo.id)
      .eq('user_id', authStore.user.id)

    if (dbError) throw dbError

    const storagePath = getStoragePath(photo.photo_url)
    if (storagePath) {
      const { error: storageError } = await supabase.storage
        .from('profile-photos')
        .remove([storagePath])

      if (storageError) {
        console.warn('Storage cleanup warning:', storageError)
      }
    }

    await refreshPhotos()

    if (photo.is_thumbnail || photo.is_primary) {
      await syncMainPhoto()
    }

    uploadProgress.value = 100
    emit('photo-uploaded', photos.value[0]?.photo_url || '')
  } catch (err) {
    console.error('Photo delete failed:', err)
    error.value = err.message || 'Failed to remove photo'
    emit('upload-error', error.value)
  } finally {
    isUploading.value = false
    uploadProgress.value = 0
    uploadStatus.value = 'Preparing upload...'
  }
}
</script>
